/**
 * Photos d'ILLUSTRATION des fiches projet (demande de Guillaume du 12/10/2026,
 * après le retour de Guillaume Casset : « Avrillé manque de photos »).
 *
 * Une illustration n'est JAMAIS une photo du projet : c'est une photo de
 * terrain prise sur un autre projet Stock CO2 comparable (même méthode, mêmes
 * essences, même région quand c'est possible, plantation d'âge voisin). Elle
 * vient compléter les vraies photos, jamais les remplacer.
 *
 * Trois règles, vérifiées par scripts/tests/illustrations-projets.test.ts :
 *   1. chaque illustration porte une légende VISIBLE qui commence par
 *      « Illustration : » et le dit (fiche publique, espace, fiche PDF) ;
 *   2. elle ne figure jamais dans src/data/project-photos.json : tous les
 *      écrans qui lisent ce fichier (cartes, couvertures, votes, Arpent…) le
 *      tiennent pour des photos du projet lui-même ;
 *   3. aucun nom de personne, de lieu-dit ou de propriétaire, ni dans la
 *      légende, ni dans le nom de fichier (public/images/illustrations-projets/).
 *
 * `provenance` est une note interne (rapport, revue) : elle n'est pas affichée.
 */

export interface IllustrationProjet {
  src: string
  alt: string
  /** Légende visible, toujours « Illustration : … ». */
  legende: string
  /** Note interne : d'où vient la photo et pourquoi elle est comparable. Jamais affichée. */
  provenance: string
}

export const PREFIXE_ILLUSTRATION = 'Illustration : '

/** Mention courte, pour une vignette où la légende entière ne tient pas. */
export const MENTION_ILLUSTRATION = 'Illustration'

const LIGNES_01: IllustrationProjet = {
  src: '/images/illustrations-projets/boisement-lignes-de-plantation-01.webp',
  alt: 'Lignes de plantation d’un jeune boisement, sur un autre projet aux mêmes essences',
  legende: 'Illustration : lignes de plantation d’un boisement aux mêmes essences, sur un projet comparable',
  provenance:
    'Projet 123 (Maine-et-Loire) : méthode Boisement, mêmes cinq essences que le projet 277 (chêne sessile, chêne pubescent, charme, pin laricio, cèdre de l’Atlas), plantation 2023.',
}

const LIGNES_02: IllustrationProjet = {
  src: '/images/illustrations-projets/boisement-lignes-de-plantation-02.webp',
  alt: 'Jeune plant dans une ligne de sol travaillé, sur un autre projet de boisement',
  legende: 'Illustration : jeune plant dans une ligne de sol travaillé, sur un projet comparable',
  provenance: 'Projet 123 (Maine-et-Loire) : même méthode, mêmes essences, même préparation du sol en lignes, plantation 2023.',
}

const JEUNE_CHENE_01: IllustrationProjet = {
  src: '/images/illustrations-projets/jeune-chene-01.webp',
  alt: 'Jeune chêne et son tuteur dans l’herbe, sur un autre projet de boisement',
  legende: 'Illustration : jeune chêne et son tuteur, sur un projet comparable',
  provenance:
    'Projet 328 (Vendée) : méthode Boisement, mêmes cinq essences (chênes sessile et pubescent, charme, pin laricio, cèdre de l’Atlas), plantation 2024.',
}

/** Illustrations par slug de fiche publique. */
export const ILLUSTRATIONS_PROJETS: Record<string, IllustrationProjet[]> = {
  // 238 et 277, Avrillé (49) : un seul boisement partagé en deux dossiers,
  // quelques vraies photos (printemps 2024) seulement.
  'le-bois-de-la-grange': [LIGNES_01, JEUNE_CHENE_01, LIGNES_02],
  'le-bois-des-quatre-vents': [LIGNES_01, JEUNE_CHENE_01, LIGNES_02],
  // 069, Côte-d'Or : jeune chênaie, deux vraies photos seulement (13/10/2026).
  'le-bois-de-la-chartreuse': [JEUNE_CHENE_01],
}

export function illustrationsDuProjet(slug: string | null | undefined): IllustrationProjet[] {
  return slug ? ILLUSTRATIONS_PROJETS[slug] ?? [] : []
}

/** Vrai pour une légende d'illustration (sert aux écrans qui ne gardent que la légende). */
export function estLegendeIllustration(legende: string | null | undefined): boolean {
  return Boolean(legende && legende.startsWith(PREFIXE_ILLUSTRATION))
}
